import type { ReactNode, Ref } from 'react'
import type { IconType } from 'react-icons'
import { LuX } from 'react-icons/lu'
import { cn } from '../../../lib/cn.ts'
import { hint } from '../../../lib/hint.ts'

export interface FilterChipProps {
  icon: IconType
  /** Icon colour class, e.g. `text-violet-fg`. */
  iconClass?: string
  /** Field name: "Priority". */
  label: string
  /** "is", "is any of", "between"… Omit for on/off filters (Overdue). */
  operator?: string
  /** The value segment (dots, avatars, names). Omit for on/off filters. */
  value?: ReactNode
  /** Plain-text value for the accessible names ("High, Urgent"). */
  valueText?: string
  /** Opens the value picker. */
  onEdit?: () => void
  onRemove: () => void
  /** Ref of the value button (the popover anchors to it). */
  editRef?: Ref<HTMLButtonElement>
  /** The value picker for this chip is open. */
  expanded?: boolean
  className?: string
}

const seg = 'inline-flex h-full items-center gap-1.5 px-2'

/**
 * Segmented filter pill (Linear/Plane style): `[icon Field] [is any of] [values] [×]`. The value
 * segment opens the picker; × removes the filter. 28px tall.
 */
export function FilterChip({
  icon: Icon,
  iconClass,
  label,
  operator,
  value,
  valueText,
  onEdit,
  onRemove,
  editRef,
  expanded,
  className,
}: FilterChipProps) {
  const summary = valueText ? `${label} ${operator ?? 'is'} ${valueText}` : label
  return (
    // biome-ignore lint/a11y/useSemanticElements: a group of related controls, not a form fieldset
    <span
      role="group"
      aria-label={`Filter: ${summary}`}
      data-filter-chip={label}
      className={cn(
        'inline-flex h-7 max-w-full min-w-0 items-stretch overflow-hidden rounded-ctl border border-border-strong bg-surface text-[12.5px] leading-none',
        className,
      )}
    >
      <span className={cn(seg, 'shrink-0 font-medium text-text')}>
        <Icon aria-hidden className={cn('size-3.5 shrink-0', iconClass)} />
        {label}
      </span>
      {operator && (
        <span className={cn(seg, 'shrink-0 border-l border-border text-muted')}>{operator}</span>
      )}
      {value !== undefined && (
        <button
          ref={editRef}
          type="button"
          onClick={onEdit}
          aria-haspopup="dialog"
          aria-expanded={expanded}
          aria-label={`Change ${label.toLowerCase()} filter: ${valueText ?? ''}`}
          className={cn(
            seg,
            'min-w-0 border-l border-border font-medium text-text transition-colors hover:bg-surface-2',
            expanded && 'bg-surface-2',
          )}
        >
          <span className="inline-flex min-w-0 items-center gap-1.5 truncate">{value}</span>
        </button>
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label.toLowerCase()} filter`}
        className="inline-flex h-full w-6.5 shrink-0 items-center justify-center border-l border-border text-muted transition-colors hover:bg-surface-2 hover:text-text"
        {...hint('Remove filter')}
      >
        <LuX aria-hidden className="size-3.5" />
      </button>
    </span>
  )
}
